//src/common/data/simcontrol.direct.js

import { formatDate } from '../../../../../../shared/utils/timeformats.js';
// const SIMCONTROL_BASE_URL = 'https://app.simcontrol.co.za';
// const SIMCONTROL_AUTH_KEY = 'u9qC7....';     

// // Helper: some providers want "Authorization: Bearer x", others "x-api-key"
// const buildHeaders = () => ({
//     'Accept': 'application/json',
//     'Content-Type': 'application/json',
//     'Authorization': `Bearer ${SIMCONTROL_AUTH_KEY}`,
//     // 'x-api-key': SIMCONTROL_AUTH_KEY,
// });

// src/common/data/simcontrol.api.js
// NOTE: This now calls our own server proxy endpoint (/simcontrol/*).
// No secrets in frontend.

const firstDayOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const lastDayOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const fetchWithTimeout = async (url, options = {}, timeoutMs = 12000) => {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);

  try {
    return await fetch(url, { ...options, signal: ctrl.signal });
  } finally {
    clearTimeout(t);
  }
};

export const getSimUsageTotals = async ({ msisdn, startDate, endDate, pageSize = 1 }) => {
  const msisdnStr = String(msisdn || "").trim();
  if (!msisdnStr || msisdnStr === "[object Object]") return null;

  const qs = new URLSearchParams({ msisdn: msisdnStr, page_size: String(pageSize) });
  if (startDate) qs.set("start_date", startDate);
  if (endDate) qs.set("end_date", endDate);

  const res = await fetchWithTimeout(`/simcontrol/usage?${qs.toString()}`, {
    headers: { Accept: "application/json" },
    cache: "no-store",
  });

  if (!res.ok) throw new Error(`SimControl usage failed (${res.status})`);

  const json = await res.json();
  const simData = json?.data?.[msisdnStr];
  if (!simData) return null;

  return {
    dataUsageMB: Number(simData.data_usage ?? 0),
  };
};

export const getSimUsageDetailsByDay = async ({ msisdn, startDate, endDate }) => {
  const msisdnStr = String(msisdn || "").trim();
  if (!msisdnStr || msisdnStr === "[object Object]") return null;

  const qs = new URLSearchParams({ msisdn: msisdnStr, page_size: "400" });
  if (startDate) qs.set("start_date", startDate);
  if (endDate) qs.set("end_date", endDate);

  const res = await fetchWithTimeout(`/simcontrol/usage-details?${qs.toString()}`, {
    headers: { Accept: "application/json" },
    cache: "no-store",
  });

  if (!res.ok) throw new Error(`SimControl usage-details failed (${res.status})`);

  const json = await res.json();
  const dataObj = json?.data || {};
  // shape: { "YYYY-MM-DD": { data_usage: "30.00", ... }, ... }

  // Return sorted [{date, dataUsageMB}]
  return Object.keys(dataObj)
    .sort()
    .map((date) => ({
      date,
      dataUsageMB: Number(dataObj?.[date]?.data_usage ?? 0),
    }));
};

export const getLast3FullMonthsTotals = async (msisdn) => {
  const now = new Date();

  // previous month = last full month
  const lastFullMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const months = [2, 1, 0].map((back) =>
    new Date(lastFullMonth.getFullYear(), lastFullMonth.getMonth() - back, 1)
  );

  const results = await Promise.all(
    months.map(async (m) => {
      const start = new Date(m.getFullYear(), m.getMonth(), 1);
      const end = new Date(m.getFullYear(), m.getMonth() + 1, 0);

      const totals = await getSimUsageTotals({
        msisdn,
        startDate: formatDate(start),
        endDate: formatDate(end),
      });

      return {
        label: m.toLocaleString("en-ZA", { month: "short" }),         // "Dec"
        fullLabel: m.toLocaleString("en-ZA", { month: "short", year: "numeric" }), // "Dec 2025"
        year: m.getFullYear(),
        monthIndex: m.getMonth(),
        startDate: formatDate(start),
        endDate: formatDate(end),
        dataUsageMB: totals?.dataUsageMB ?? 0,
      };
    })
  );

  const avg = results.reduce((a, x) => a + (x.dataUsageMB || 0), 0) / (results.length || 1);

  // For your UI label e.g. "Dec 2025–Feb 2026"
  const rangeLabel = results.length
    ? `${results[0].fullLabel}–${results[results.length - 1].fullLabel}`
    : "";

  return { months: results, avgLast3MB: avg, rangeLabel };
};

export const getThisMonthDailyUsage = async (msisdn) => {
  const today = new Date();
  const start = firstDayOfMonth(today);
  const end = today;

  const rows = await getSimUsageDetailsByDay({
    msisdn,
    startDate: formatDate(start),
    endDate: formatDate(end),
  });

  // spark labels: day-of-month "01..31"
  const labels = rows.map((r) => r.date.slice(8, 10));
  const values = rows.map((r) => r.dataUsageMB);

  return { labels, values, rows };
};

export const getSimDataUsage = async (input) => {
  const msisdn =
    typeof input === "string"
      ? input
      : (input?.msisdn || input?.simCardNumber || input?.simNumber || "");

  const msisdnStr = String(msisdn).trim();

  if (!msisdnStr || msisdnStr === "[object Object]") return null;

  const url = `/simcontrol/usage?msisdn=${encodeURIComponent(msisdnStr)}&page_size=1`;

  const res = await fetchWithTimeout(url, {
    method: "GET",
    headers: { "Accept": "application/json" },
    cache: "no-store",
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`SimControl proxy failed (${res.status}): ${txt}`);
  }

  const json = await res.json();
  const simData = json?.data?.[msisdnStr];
  if (!simData) return null;

  return {
    dataUsageMB: simData.data_usage,
  };
};

export const getUsageDetailsRange = async ({ msisdn, startDate, endDate, page_size = 200 }) => {
  const qs = new URLSearchParams({
    msisdn,
    start_date: startDate,
    end_date: endDate,
    page_size: String(page_size)
  });

  const res = await fetch(`/simcontrol/usage-details?${qs.toString()}`, {
    method: "GET",
    headers: { "Accept": "application/json" },
    cache: "no-store"
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`SimControl usage-details failed (${res.status}): ${txt}`);
  }

  const json = await res.json();
  const data = json?.data || {};

  // data shape: { "2024-01-01": { data_usage: "30.00", ... }, ... }
  const dates = Object.keys(data).sort();

  // Fill missing days between start/end with 0
  const start = new Date(startDate);
  const end = new Date(endDate);

  const labels = [];
  const values = [];
  const fullDates = [];

  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const key = `${yyyy}-${mm}-${dd}`;

    const v = Number(data?.[key]?.data_usage ?? 0);

    labels.push(String(d.getDate()));
    values.push(isFinite(v) ? v : 0);
    fullDates.push(key);
  }

  return { labels, values, dates: fullDates };
};

// export const getSimUsageTotals = async ({ msisdn, startDate, endDate, pageSize = 1 }) => {
//     const msisdnStr = String(msisdn || "").trim();
//     if (!msisdnStr || msisdnStr === "[object Object]") return null;

//     const qs = new URLSearchParams({
//         msisdn: msisdnStr,
//         page_size: String(pageSize),
//     });

//     if (startDate) qs.set("start_date", startDate);
//     if (endDate) qs.set("end_date", endDate);

//     const url = `/simcontrol/usage?${qs.toString()}`;

//     const res = await fetch(url, {
//         method: "GET",
//         headers: { Accept: "application/json" },
//         cache: "no-store",
//     });

//     if (!res.ok) {
//         const txt = await res.text().catch(() => "");
//         throw new Error(`SimControl proxy failed (${res.status}): ${txt}`);
//     }

//     const json = await res.json();
//     const simData = json?.data?.[msisdnStr];
//     if (!simData) return null;

//     return {
//         msisdn: msisdnStr,
//         dataUsageMB: Number(simData.data_usage ?? 0)
//     };
// };

// // helper to generate 3 full-month ranges ending with "this month"
// const monthRange = (year, monthIndex0) => {
//     // monthIndex0: 0=Jan
//     const start = new Date(year, monthIndex0, 1);
//     const end = new Date(year, monthIndex0 + 1, 0); // last day of month
//     return { start, end };
// };

// export const getSimUsageLast3Months = async (msisdn) => {
//     const now = new Date();
//     const ranges = [];

//     for (let i = 2; i >= 0; i--) {
//         const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
//         const { start, end } = monthRange(d.getFullYear(), d.getMonth());
//         ranges.push({ start, end });
//     }

//     const results = await Promise.all(
//         ranges.map(async (r) => {
//             const totals = await getSimUsageTotals({
//                 msisdn,
//                 startDate: formatDate(r.start),
//                 endDate: formatDate(r.end),
//             });

//             const label = r.start.toLocaleString("en-ZA", { month: "short" }); // "Jan"
//             return {
//                 label,
//                 startDate: formatDate(r.start),
//                 endDate: formatDate(r.end),
//                 dataUsageMB: totals?.dataUsageMB ?? 0,
//                 airtimeUsage: totals?.airtimeUsage ?? 0,
//             };
//         })
//     );

//     return results;
// };
